/**
 * initFakeSelects
 * - работает без зависимостей
 * - поддерживает несколько селектов на странице
 * - закрывает меню по клику вне, по Esc
 * - обновляет hidden input + label
 *
 * HTML-атрибуты:
 *  container: [data-fake-select]
 *  trigger:   [data-fake-select-trigger]
 *  menu:      [data-fake-select-menu]
 *  label:     [data-fake-select-label]
 *  value:     [data-fake-select-value]
 *  option:    [data-fake-select-option][data-value]
 */
export default function initFakeSelects(root = document) {
    const selects = Array.from(root.querySelectorAll('[data-fake-select]'));
    if (!selects.length) return;
    
    const closeAll = (exceptEl = null) => {
        selects.forEach((el) => {
            if (exceptEl && el === exceptEl) return;
            closeSelect(el);
        });
    };
    
    const openSelect = (el) => {
        const trigger = el.querySelector('[data-fake-select-trigger]');
        const menu = el.querySelector('[data-fake-select-menu]');
        if (!trigger || !menu) return;
        
        closeAll(el);
        
        menu.classList.remove('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        el.dataset.open = '1';
    };
    
    const closeSelect = (el) => {
        const trigger = el.querySelector('[data-fake-select-trigger]');
        const menu = el.querySelector('[data-fake-select-menu]');
        if (!trigger || !menu) return;
        
        menu.classList.add('hidden');
        trigger.setAttribute('aria-expanded', 'false');
        delete el.dataset.open;
    };
    
    const toggleSelect = (el) => {
        if (el.dataset.open === '1') closeSelect(el);
        else openSelect(el);
    };
    
    const setValue = (el, value, labelText) => {
        const hidden = el.querySelector('[data-fake-select-value]');
        const label = el.querySelector('[data-fake-select-label]');
        if (hidden) hidden.value = value ?? '';
        if (label && typeof labelText === 'string') label.textContent = labelText;
        
        // Триггерим change, чтобы твоя форма/валидация могла реагировать
        if (hidden) {
            hidden.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };
    
    // Initialization for each select
    selects.forEach((el) => {
        const trigger = el.querySelector('[data-fake-select-trigger]');
        const menu = el.querySelector('[data-fake-select-menu]');
        if (!trigger || !menu) return;
        
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            toggleSelect(el);
        });
        
        // Click on the option
        el.addEventListener('click', (e) => {
            const option = e.target.closest('[data-fake-select-option]');
            if (!option || !el.contains(option)) return;
            
            e.preventDefault();
            const value = option.getAttribute('data-value') ?? '';
            const labelText = option.textContent?.trim() ?? '';
            
            setValue(el, value, labelText);
            
            // aria-selected
            el.querySelectorAll('[data-fake-select-option]').forEach((btn) => {
                btn.setAttribute('aria-selected', 'false');
                btn.classList.remove(btn.dataset.selectedClass);
            });
            
            option.classList.add(option.dataset.selectedClass);
            option.setAttribute('aria-selected', 'true');
            
            closeSelect(el);
            trigger.focus();
        });
        
        // Esc close
        el.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                closeSelect(el);
                trigger.focus();
            }
        });
    });
    
    // Click outside to close all
    document.addEventListener('click', (e) => {
        const clickedInside = e.target.closest('[data-fake-select]');
        if (!clickedInside) closeAll();
    });
    
    // Scroll/resize — close all (good for UX)
    window.addEventListener('scroll', () => closeAll(), true);
    window.addEventListener('resize', () => closeAll());
}